import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Container,
  MyTextInput,
  Spacing,
  Typography,
} from 'components/src';
import {
  Platform,
  createUseStyles,
  routesName,
  setToken,
  storeToken,
  useGetLoginCaptcha,
  useLang,
  usePostLogin,
  usePostLoginCheckCaptcha,
  usePostLoginCheckCode,
} from 'core/src';
import { Image, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { ParamList } from '../../app/RouteConfig/jsdoc';
import { StackScreenProps } from '@react-navigation/stack';
import FontAwesome from 'react-native-vector-icons/FontAwesome';
import { useToast } from 'react-native-toast-notifications';
import AsyncStorage from '@react-native-async-storage/async-storage';

const FontAwesomeX = FontAwesome as unknown as React.ComponentType<any>;

const Login = ({
  navigation,
}: StackScreenProps<ParamList, routesName.LOGIN>) => {
  const { styles, colors, theme } = useStyles();
  const { lang } = useLang();
  const { i18n } = useTranslation();
  const [userName, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [captcha, setCaptcha] = useState<string>('');
  const [code, setCode] = useState<string>('');
  const [tokenCaptcha, setTokenCaptcha] = useState<string>('');
  const [typeInput, setTypeInput] = useState<boolean>(true);
  const [typeInputName, setTypeInputName] = useState<boolean>(false);

  const [step, setStep] = useState(1);
  const dispatch = storeToken.useDispatch();
  const toast = useToast();

  const { data, refetch } = useGetLoginCaptcha({
    enabled: false,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  useEffect(() => {
    step === 2 && refetch();
  }, [step, refetch]);

  const url = data?.data?.files
    ?.map((image: string) => `data:image/png;base64,${image}`)
    .reverse();

  const { mutate, isPending } = usePostLogin({
    onSuccess: async (data) => {
      try {
        const accessToken = data?.tokens?.data?.accessToken;
        const checkPassword = data?.changedPassword;
        await AsyncStorage.setItem('checkPassword', checkPassword);
        dispatch(setToken(accessToken));
        if (Platform.isDesktop) {
          navigation.navigate(routesName.HOME);
        } else {
          navigation.navigate(routesName.CONVERSATIONS);
        }
        // (Platform.isDesktop || Platform.isPwa) && window.location.reload();
      } catch (error) {
        console.error('Failed to save the token to AsyncStorage', error);
      }
    },
    onError: () => {
      toast.show(i18n.t('yourInformationsAreFalse'), { type: 'danger' });
    },
  });

  const userLogin = useCallback(() => {
    mutate({
      requestBody: {
        userName,
        password,
      },
    });
  }, [userName, password]);

  useEffect(() => {
    const handleKeyDown = (event: any) => {
      if (event.key === 'Enter') {
        userLogin();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [userName, password]);

  const { mutate: captchaMutate, isPending: captchaPending } =
    usePostLoginCheckCaptcha({
      onSuccess: (data) => {
        setStep(3);
        setTokenCaptcha(data?.data);
        toast.show(i18n.t('کد تایید برای شما ارسال شد'), { type: 'success' });
      },
    });

  const confirmCaptcha = useCallback(() => {
    captchaMutate({
      requestBody: {
        userName,
        captcha: captcha,
        captchaToken: data?.data?.token,
      },
    });
  }, [userName, captcha]);

  const { mutate: codeMutate, isPending: codePending } = usePostLoginCheckCode({
    onSuccess: () => {
      setStep(1);
      toast.show(i18n.t('رمز عبور جدید برای شما ارسال شد'), {
        type: 'success',
      });
    },
  });

  const confirmCode = useCallback(() => {
    codeMutate({
      requestBody: {
        registerToken: tokenCaptcha,
        registerCode: code,
      },
    });
  }, [tokenCaptcha, code]);

  return (
    <Container>
      <View style={styles.logo}>
        <Image
          source={require('../../../../assets/src/images/logo.png')}
          style={styles.image}
          resizeMode="contain"
        />
      </View>
      {step === 1 && (
        <View style={[styles.mainView]}>
          <Spacing vertical={20} />
          <View style={styles.loginContainer}>
            <Typography
              text={i18n.t('login')}
              size={30}
              weight="bold"
              color={colors.secondryColor}
            />
          </View>
          <Spacing vertical={40} />
          <MyTextInput
            inputWidth={'35%'}
            onSubmit={userLogin}
            classes={styles.input}
            type={typeInputName}
            inlineImageLeft={
              <FontAwesomeX
                name="user"
                size={25}
                color={colors.secondryColorOpacity}
              />
            }
            inlineImageRight={
              <FontAwesomeX
                name={typeInputName ? 'eye' : 'eye-slash'}
                size={23}
                color={colors.secondryColorOpacity}
                onPress={() => setTypeInputName(!typeInputName)}
              />
            }
            placeholderTextColor={colors.secondryColorOpacity}
            placeholder={i18n.t('userName')}
            change={(text: string) => {
              setUsername(text);
            }}
          />
          <Spacing vertical={10} />
          <MyTextInput
            inputWidth={'35%'}
            onSubmit={userLogin}
            classes={styles.input}
            inlineImageLeft={
              <FontAwesomeX
                name="lock"
                size={25}
                color={colors.secondryColorOpacity}
              />
            }
            inlineImageRight={
              <FontAwesomeX
                name={typeInput ? 'eye' : 'eye-slash'}
                size={23}
                color={colors.secondryColorOpacity}
                onPress={() => setTypeInput(!typeInput)}
              />
            }
            placeholderTextColor={colors.secondryColorOpacity}
            placeholder={i18n.t('password')}
            type={typeInput}
            change={(text: string) => {
              setPassword(text);
            }}
          />
          <Spacing vertical={20} />
          <View style={styles.buttonView}>
            <Button
              classes={styles.button}
              onClick={userLogin}
              isLoading={isPending}
            >
              <Typography
                text={isPending ? '' : i18n.t('login')}
                color={colors.container}
              />
            </Button>
          </View>

          <Spacing vertical={20} />
          <Button
            onClick={() => {
              setStep(2);
            }}
            isLoading={isPending}
          >
            <Typography
              color={colors.secondryColor}
              size={12}
              text={i18n.t('forgetPassword')}
            />
          </Button>
        </View>
      )}

      {/* ForgetPassword */}
      {step === 2 && (
        <View style={[styles.mainView]}>
          <Spacing vertical={20} />
          <View style={styles.loginContainer}>
            <Typography
              text={i18n.t('forgetPassword')}
              size={30}
              weight="bold"
              color={colors.secondryColor}
            />
          </View>
          <Spacing vertical={40} />

          <MyTextInput
            focus
            inputWidth={'35%'}
            onSubmit={confirmCaptcha}
            classes={styles.input}
            inlineImageLeft={
              <FontAwesomeX
                name="user"
                size={25}
                color={colors.secondryColorOpacity}
              />
            }
            placeholderTextColor={colors.secondryColorOpacity}
            placeholder={i18n.t('userName')}
            change={(text: string) => {
              setUsername(text);
            }}
          />
          <Spacing vertical={10} />
          <View
            //@ts-ignore
            style={{
              flexDirection: lang === 'fa-IR' ? 'row ' : 'row-reverse',
              width: 150,
              height: 50,
              backgroundColor: theme === 'white' ? '#ddd' : '#fff',
              justifyContent: 'center',
              marginVertical: 10,
              alignItems: 'center',
            }}
          >
            {url?.map((image: string) => (
              <Image
                source={{
                  uri: image,
                }}
                resizeMode="contain"
                style={{ height: 30, width: 40 }}
              />
            ))}
          </View>
          <Spacing vertical={10} />
          <MyTextInput
            onSubmit={confirmCaptcha}
            inputWidth={'35%'}
            classes={styles.input}
            inlineImageLeft={
              <FontAwesomeX
                name="key"
                size={25}
                color={colors.secondryColorOpacity}
              />
            }
            placeholderTextColor={colors.secondryColorOpacity}
            placeholder={i18n.t('captcha')}
            change={(text: string) => {
              setCaptcha(text);
            }}
          />

          <Spacing vertical={20} />
          <View style={styles.buttonView}>
            <Button
              classes={styles.button}
              onClick={confirmCaptcha}
              isLoading={captchaPending}
            >
              <Typography
                text={isPending ? '' : i18n.t('recivedCode')}
                color={colors.container}
              />
            </Button>
          </View>
          <Spacing vertical={20} />
          <Button
            onClick={() => {
              setStep(1);
            }}
            isLoading={isPending}
          >
            <Typography
              color={colors.secondryColor}
              size={12}
              text={i18n.t('back')}
            />
          </Button>
        </View>
      )}

      {step === 3 && (
        <View style={[styles.mainView]}>
          <Spacing vertical={20} />
          <View style={styles.loginContainer}>
            <Typography
              text={i18n.t('forgetPassword')}
              size={30}
              weight="bold"
              color={colors.secondryColor}
            />
          </View>
          <Spacing vertical={40} />

          <MyTextInput
            inputWidth={'35%'}
            focus
            onSubmit={confirmCode}
            classes={styles.input}
            inlineImageLeft={
              <FontAwesomeX
                name="key"
                size={25}
                color={colors.secondryColorOpacity}
              />
            }
            placeholderTextColor={colors.secondryColorOpacity}
            placeholder={i18n.t('code')}
            change={(text: string) => {
              setCode(text);
            }}
          />

          <Spacing vertical={20} />
          <View style={styles.buttonView}>
            <Button
              classes={styles.button}
              onClick={confirmCode}
              isLoading={codePending}
            >
              <Typography
                text={isPending ? '' : i18n.t('changePassword')}
                color={colors.container}
              />
            </Button>
          </View>
          <Spacing vertical={20} />
          <Button
            onClick={() => {
              setStep(2);
              setCaptcha('');
            }}
          >
            <Typography
              color={colors.secondryColor}
              size={12}
              text={i18n.t('back')}
            />
          </Button>
        </View>
      )}
    </Container>
  );
};

const useStyles = createUseStyles(({ colors, theme }) => ({
  mainView: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    paddingHorizontal: 30,
    marginVertical: 100,
    ...Platform.select({
      desktop: {
        alignItems: 'center',
      },
    }),
  },
  logo: {
    marginVertical: 40,
    paddingHorizontal: 30,
  },
  image: {
    width: '100%',
    height: 60,
  },
  title: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  button: {
    backgroundColor: colors.secondryColor,
    width: 150,
    padding: 8,
    textAlign: 'center',
    alignItems: 'center',
    borderRadius: 5,
    elevation: 3,
  },
  buttonView: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  loginContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  input: {
    backgroundColor: colors.container,
  },
}));

export { Login };
